/**
 * Pure-data English catalogue for namespace 'pec' (W1-08 formats-legacy-respack; imports only core/util/types):
 * import report entries of `importPec` (PhiEditer charts). Params are named in braces: `sourceLine` is the 1-based
 * line of the .pec file, `token` the offending text, `channel` one of alpha / x / y / rotate (ICU `select`).
 * The import report shows an entry's `count` next to the text.
 */

import type { MessageCatalog } from '../../core/util/types';

const CHANNEL = '{channel, select, alpha {opacity} x {X position} y {Y position} rotate {rotation} other {{channel}}}';

const catalog: MessageCatalog = {
  ns: 'pec',
  locale: 'en',
  messages: {
    // ---- Lexer errors: Phira refuses the whole chart (the command was skipped) ----
    'report.bom':
      'The chart file starts with a byte order mark (BOM). It was ignored; Phira cannot read the offset with it.',
    'report.offsetMissing':
      'The first line of the chart should hold the offset in milliseconds but is empty. 150 (no offset) was used.',
    'report.offsetInvalid':
      'The first line of the chart should hold the offset in milliseconds but holds "{token}". 150 (no offset) was used.',
    'report.offsetExtra':
      'The first line of the chart has more than the offset ("{token}"). The rest was ignored; Phira refuses such charts.',
    'report.unknownCommand': 'Line {sourceLine}: "{token}" is not a PEC command. The line was skipped.',
    'report.missingArgument': 'Line {sourceLine}: the command is missing a value. The line was skipped.',
    'report.expectedNumber': 'Line {sourceLine}: "{token}" is not a number. The line was skipped.',
    'report.expectedIndex': 'Line {sourceLine}: "{token}" is not a whole number of 0 or more. The line was skipped.',
    'report.expectedEasing':
      'Line {sourceLine}: the easing "{token}" is not a whole number from 0 to 255. The line was skipped.',
    'report.expected01': 'Line {sourceLine}: the fake flag "{token}" must be 0 or 1. The line was skipped.',
    'report.unexpectedExtra':
      'Line {sourceLine}: "{token}" after the command was ignored. Phira refuses lines with extra values.',
    'report.bpAfterCommands':
      'Line {sourceLine}: a BPM change (bp) comes after notes or events. It was used anyway; Phira requires every bp line first.',
    'report.noNotesInserted': 'Line {sourceLine}: a speed (#) or size (&) line comes before any note. It was skipped.',

    // ---- Other things Phira refuses or crashes on (repaired) ----
    'report.noBpm': 'The chart has no BPM (bp) line, which makes Phira crash. 120 BPM was used.',
    'report.missingChannel': `A judge line has no ${CHANNEL} value at all, which makes Phira crash. It was left unset.`,
    'report.missingSpeed':
      'A judge line has no speed (cv) value at all, which makes Phira crash. Its notes do not move.',
    'report.motionBeforeValue': `Line {sourceLine}: a ${CHANNEL} movement comes before any starting value, which Phira refuses. Its target value is used from the start.`,

    // ---- Changed (rendering may differ slightly) ----
    'report.lineIndexTooLarge':
      'Line {sourceLine}: the judge line number {token} is above {max}, the most the editor imports (every number creates all judge lines up to it). The line was skipped; Phira would load it.',
    'report.invalidBpm': 'Line {sourceLine}: the BPM {bpm} is not a positive number. The line was ignored.',
    'report.bpmUnsorted':
      'The BPM changes are not in order. They were sorted and every time was converted through seconds the way Phira computes them.',
    'report.reversedMotion': 'Line {sourceLine}: a movement ends before it starts. It was made instant.',
    'report.unsortedSpeed':
      'Speed changes (cv) are not in time order. They were converted the way Phira plays them forward.',
    'report.speedJump':
      'Out-of-order speed changes make the note distance jump in Phira. The jump cannot be expressed with speed and was smoothed out.',
    'report.negativeHold': 'Line {sourceLine}: a hold note ends before it starts. It was made zero-length.',
    'report.visibleTimeInexact':
      'PE alpha codes show some notes, hide them and show them again before they are hit. Visible times keep only the last stretch before each hit.',
    'report.alphaCrossing':
      'A fade passes through PE alpha codes at times that are not exact beats. The changes were placed at the nearest beat.',
    'report.inexactTimes': 'Some times could not be expressed exactly in beats and were rounded.',
    'report.timeOutOfRange':
      'Some beats are so large or so far before the start that a chart cannot hold them. They were moved to the nearest beat a chart can hold.',

    // ---- Normalised (structure changed, Phira plays it the same) ----
    'report.bpmShifted':
      'The first BPM change is at beat {beat} instead of 0. Every time was shifted so that it is at beat 0; Phira plays it the same.',
    'report.bpmDuplicate': 'Two BPM changes are at the same beat. The later one was kept, as Phira uses it.',
    'report.overlapClipped': `Overlapping ${CHANNEL} events were trimmed the way Phira trims them.`,
    'report.alphaCodes':
      'Judge lines use PE alpha codes (negative opacity). They were converted to opacity and note visibility that Phira shows the same way.',
    'report.visibleTime':
      'Notes got a visible time from a PE alpha code that makes them appear shortly before they are hit.',
    'report.beatsSnapped':
      'Beats written as decimals (such as 54.984) were stored as the exact fraction they stand for (6873/125). Phira reads them as 32-bit numbers and cannot tell the two apart; times move by at most {maxUs} microseconds.',
    'report.lineShape':
      'Judge lines got a length of 65.17 % and a thickness of 1.0002 because Phira draws PhiEditer lines shorter and thinner than RPE lines.',

    // ---- Flagged (kept exactly) ----
    'report.holdTailFrozen':
      'A hold ends more than one second after the last note or event. Phira stops the line movement there, so its end is drawn shorter; the import keeps that behaviour.',
  },
};

export default catalog;
